import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { Artist } from '@shared/models/artist';
import { Interest } from '@shared/models/interest';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import { ProductType } from '@shared/models/product-type';
import { ServerFavourite } from '@shared/models/server-favourite';
import { PreferencesService } from '@shared/services/preferences.service';
import { findLast } from '@shared/utils/find-last';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  LoadArtistsFailureAction,
  LoadArtistsSuccessAction,
  LoadFavouritesFailureAction,
  LoadFavouritesNextPageAction,
  LoadFavouritesSuccessAction,
  LoadPrimaryInterestsFailureAction,
  LoadPrimaryInterestsSuccessAction,
  LoadPrimaryInterestsSuccessActionPayload,
  PreferencesAction,
  PreferencesActionTypes,
} from '../actions/preferences.actions';
import { PreferencesState } from '../reducers';
import { selectArtistState, selectFavouritesState, selectFollowingFilter } from '../selectors';
import { selectPrimaryInterestsState } from '../selectors/primary-interests.selector';

@Injectable()
export class PreferencesEffects {
  public isSelected$: Observable<boolean>;

  private favouriteTypesNotificationTextsOn = {
    EntertainmentEvents: 'Preferences:EventAddedNotification',
    Brand: 'Preferences:BrandAddedNotification',
  };
  private favouriteTypesAriaLabelsOn = {
    EntertainmentEvents: 'Preferences:ReaderEventAdded',
    Brand: 'Preferences:ReaderBrandAdded',
  };

  private favouriteTypesNotificationTextsOff = {
    EntertainmentEvents: 'Preferences:EventRemovedNotification',
    Brand: 'Preferences:BrandRemovedNotification',
  };
  private favouriteTypesAriaLabelsOff = {
    EntertainmentEvents: 'Preferences:ReaderEventRemoved',
    Brand: 'Preferences:ReaderBrandRemoved',
  };

  private favouriteTrackEventName = {
    EntertainmentEvents: 'Preferences_Favourite event selected preferences page',
    Brand: 'Preferences_Favourite brand selected preferences page',
  };

  constructor(
    private store: Store<PreferencesState>,
    private ngrxStore: Store<PreferencesState>,
    private snackBar: MatSnackBar,
    private actions$: Actions<PreferencesAction>,
    private preferencesService: PreferencesService,
    private angulartics2Segment: Angulartics2Segment
  ) {
    this.isSelected$ = this.store.select(selectFollowingFilter);
  }

  public trackFollowingFilterChangeEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PreferencesActionTypes.ARTIST_FOLLOWING_FILTER_CHANGE),
        withLatestFrom(this.ngrxStore.pipe(select(selectFollowingFilter))),
        tap(([, artistFollowingFilter]) => {
          const eventName = artistFollowingFilter
            ? 'Following artists only filter selected'
            : 'Following artists only filter cleared';
          this.angulartics2Segment.eventTrack(eventName, {
            page_url: window.location.href,
          });
        })
      ),
    { dispatch: false }
  );

  public loadArtists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PreferencesActionTypes.LOAD_ARTISTS,
        PreferencesActionTypes.LOAD_ARTISTS_NEXT_PAGE,
        PreferencesActionTypes.ARTIST_INITIAL_FILTER_CHANGE,
        PreferencesActionTypes.ARTIST_SEARCH_TERM_CHANGE,
        PreferencesActionTypes.ARTIST_FOLLOWING_FILTER_CHANGE
      ),
      withLatestFrom(this.ngrxStore.pipe(select(selectArtistState))),
      switchMap(([, artistState]) => {
        const after = artistState.ids[artistState.ids.length - 1] as number;

        return this.preferencesService
          .searchArtists(artistState.initialFilter, artistState.searchTerm, artistState.followingFilter, after)
          .pipe(
            map((res: PaginatedApiResponse<Artist>) => new LoadArtistsSuccessAction(res.results, !!res.next)),
            catchError(() => of(new LoadArtistsFailureAction()))
          );
      })
    )
  );

  public loadFavourites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreferencesActionTypes.LOAD_FAVOURITES, PreferencesActionTypes.LOAD_FAVOURITES_NEXT_PAGE),
      withLatestFrom(this.ngrxStore.pipe(select(selectFavouritesState))),
      switchMap(([action, favouritesState]) => {
        let lastId: string;
        let lastType: ProductType | null;

        if (action instanceof LoadFavouritesNextPageAction) {
          lastId = findLast(favouritesState.ids as Array<string>, (id) => favouritesState.entities[id].preferred);
          lastType = favouritesState.entities[lastId]?.type;
        }

        return this.preferencesService.getFavourites(action.productTypes, +lastId?.split('-')[0], lastType).pipe(
          map((res: PaginatedApiResponse<ServerFavourite>) => {
            const results = res.results.map((favourite: ServerFavourite) => {
              return {
                ...favourite,
                preferred: true,
                notificationTexts: {
                  labels: {
                    on: this.favouriteTypesNotificationTextsOn[favourite.type.toString()],
                    off: this.favouriteTypesNotificationTextsOff[favourite.type.toString()],
                    params: {
                      event_name: favourite.name,
                      brand: favourite.name,
                    },
                  },
                  ariaLabels: {
                    on: this.favouriteTypesAriaLabelsOn[favourite.type.toString()],
                    off: this.favouriteTypesAriaLabelsOff[favourite.type.toString()],
                  },
                },
                preferenceTracking: {
                  eventName: this.favouriteTrackEventName[favourite.type.toString()],
                  data: {
                    id: favourite.id,
                    name: favourite.name,
                  },
                },
              };
            });
            return { ...res, results };
          }),
          map((response) => new LoadFavouritesSuccessAction(response.results, !!response.next)),
          catchError(() => of(new LoadFavouritesFailureAction()))
        );
      })
    )
  );

  public error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PreferencesActionTypes.LOAD_ARTISTS_FAILURE, PreferencesActionTypes.LOAD_FAVOURITES_FAILURE),
        tap(() => {
          this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 5000 });
        })
      ),
    { dispatch: false }
  );

  public loadPrimaryInterest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PreferencesActionTypes.LOAD_PRIMARY_INTERESTS,
        PreferencesActionTypes.LOAD_PRIMARY_INTERESTS_NEXT_PAGE,
        PreferencesActionTypes.PRIMARY_INTERESTS_SEARCH_TERM_CHANGE,
        PreferencesActionTypes.PRIMARY_INTERESTS_CATEGORY_FILTER
      ),
      withLatestFrom(this.ngrxStore.pipe(select(selectPrimaryInterestsState))),
      switchMap(([action, primaryInterestState]) => {
        const pageSize = 25;

        let page: number;
        if (action.payload.resetPagination) {
          page = 1;
        } else {
          page = primaryInterestState.nextPageIndex || 1;
        }

        return this.preferencesService
          .searchPrimaryInterest(pageSize, page, primaryInterestState.searchPhrase, primaryInterestState.selectedFilter)
          .pipe(
            map((res: PaginatedApiResponse<Interest>) => {
              const payload: LoadPrimaryInterestsSuccessActionPayload = {
                primaryInterests: res.results,
                nextPage: !!res.next,
                nextPageIndex: null,
              };

              if (res.next) {
                const url = new URL(res.next);
                const params = new URLSearchParams(url.search);
                payload.nextPageIndex = parseInt(params.get('page'));
              }

              return new LoadPrimaryInterestsSuccessAction(payload);
            }),
            catchError(() => of(new LoadPrimaryInterestsFailureAction()))
          );
      })
    )
  );
}
